// Auth helpers (token storage + session state). Pure utilities, no API calls.
// HTTP call for login lives in `src/pages/auth/api.js` per project convention.
// Token storage primitives live in `src/utils/tokenStorage.js` so they can be
// imported anywhere without pulling the rest of this module.

import { clearAllowedPlatform } from '../../../utils/loginMeta'
import { permissionStorage } from '../../../utils/permissionStorage'
import { disconnectPusher } from '../../../utils/pusher'
import { tokenStorage, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../../../utils/tokenStorage'
import { userStorage } from '../../../utils/userStorage'

export { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, tokenStorage }

// Decode a JWT payload without signature verification — used only for client-side expiry checks.
function decodeJwtPayload(token) {
  if (!token) return null
  try {
    const [, payload] = token.split('.')
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return null
  }
}

export function isTokenExpired(token) {
  const payload = decodeJwtPayload(token)
  // Server is the source of truth for token validity. If the token isn't a
  // parseable JWT (no `exp` claim) don't reject it client-side — the API will
  // bounce an invalid token with 401 and the refresh interceptor will handle
  // it. Only fail closed when we can prove the token is past its `exp`.
  if (!payload?.exp) return false
  return Date.now() >= payload.exp * 1000
}

export function isAuthenticated() {
  const token = tokenStorage.getAccessToken()
  return Boolean(token) && !isTokenExpired(token)
}

export function logout() {
  // Tear down the Pusher websocket first so it doesn't outlive the
  // session — the singleton keeps the bearer token in its auth endpoint
  // headers, so leaving it connected after a logout would leak the
  // just-cleared credentials to any future subscriber on the same page.
  disconnectPusher()
  tokenStorage.clearTokens()
  userStorage.clearUserDetails()
  userStorage.clearSelectedBranch()
  // The cached branch permissions belong to the session that just ended —
  // leaving them behind would hand them to the next user on this browser.
  permissionStorage.clearPermissions()
  clearAllowedPlatform()
}
